import { useState, useEffect, useCallback } from 'react';
import type { FC, ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePermissions } from '../../hooks/usePermissions';
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  TextField,
  MenuItem,
  Typography,
  IconButton,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Tooltip,
  useMediaQuery,
  useTheme,
  Stack
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  GetApp as DownloadIcon,
  PictureAsPdf as PdfIcon
} from '@mui/icons-material';
import { Contact, ContactFilters } from '../../types/contact';
import { contactsApi, branchesApi } from '../../services/api';
import { Branch } from '../../types/branch';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

// Function to export data to Excel file
const exportToExcel = (data: any[], filename: string) => {
  // Create a new workbook
  const wb = XLSX.utils.book_new();
  
  // Convert data to worksheet
  const ws = XLSX.utils.json_to_sheet(data);
  
  // Add worksheet to workbook
  XLSX.utils.book_append_sheet(wb, ws, "Contacts");
  
  // Generate Excel binary
  const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  
  // Create a Blob and trigger download
  const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  saveAs(blob, filename);
};

interface ContactListProps {
  defaultType?: 'customer' | 'supplier';
  hideTypeFilter?: boolean;
}

const ContactList: FC<ContactListProps> = ({ defaultType, hideTypeFilter }): ReactElement => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));
  const { hasPermission } = usePermissions();
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [total, setTotal] = useState(0);
  // Loading state can be used to show a spinner later
  const [isLoading, setIsLoading] = useState(false);
  const [filters, setFilters] = useState<ContactFilters>({
    page: 1,
    limit: 10,
    type: defaultType
  });

  // Load contacts on mount and when filters change
  useEffect(() => {
    fetchContacts();
  }, [filters]);

  // Load branches on mount
  useEffect(() => {
    const loadBranches = async () => {
      try {
        const response = await branchesApi.getBranches({ page: 1, limit: 100 });
        if (response && response.branches) {
          setBranches(response.branches);
        } else {
          setBranches([]);
        }
      } catch (err) {
        console.error('Error loading branches', err);
      }
    };

    loadBranches();
  }, []);

  // Fetch contacts from API with current filters
  const fetchContacts = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await contactsApi.getContacts(filters);
      setContacts(response.contacts);
      setTotal(response.pagination.total);
    } catch (err) {
      console.error('Error fetching contacts', err);
    } finally {
      setIsLoading(false);
    }
  }, [filters]);

  // Handle filter changes
  const handleFilterChange = (key: string, value: any) => {
    // Reset page to 1 when changing filters
    setFilters(prev => ({ ...prev, [key]: value, page: 1 }));
  };

  // Handle pagination
  const handlePageChange = (event: any, newPage: number) => {
    setFilters(prev => ({ ...prev, page: newPage + 1 }));
  };

  // Handle rows per page change
  const handleLimitChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFilters(prev => ({
      ...prev,
      limit: parseInt(event.target.value, 10),
      page: 1
    }));
  };

  // Handle contact deletion
  const handleDelete = async (id: string) => {
    if (!window.confirm('هل أنت متأكد من رغبتك في حذف جهة الاتصال هذه؟')) {
      return;
    }

    try {
      await contactsApi.deleteContact(id);
      // Refresh the contact list
      fetchContacts();
    } catch (err) {
      console.error('Error deleting contact', err);
      alert('حدث خطأ أثناء حذف جهة الاتصال');
    }
  };

  // Export contacts to Excel
  const handleExcelExport = async () => {
    try {
      // جلب جميع البيانات المفلترة (بدون ترقيم صفحات)
      const exportFilters = { ...filters, page: 1, limit: total || 10000 };
      const response = await contactsApi.getContacts(exportFilters);
      const allContacts = response.contacts;
      
      // Enhanced data transformation with Arabic labels
      const enhancedData = allContacts.map(contact => {
        return {
          "الاسم": contact.name,
          "النوع": contact.type === 'customer' ? 'عميل' : 'مورد',
          "البريد الإلكتروني": contact.email || '-',
          "الهاتف": contact.phone || '-',
          "رقم الحساب": typeof contact.accountId === 'object' && contact.accountId?.accountNumber
            ? contact.accountId.accountNumber
            : (contact.linkedAccount ? 'مرتبط' : '-'),
          "الفئة": contact.category === 'regular' ? 'عادي' :
                  contact.category === 'premium' ? 'مميز' : 'VIP',
          "الحالة": contact.status === 'active' ? 'نشط' : 'غير نشط',
          "العنوان": `${contact.address?.city || ''} ${contact.address?.country || ''}`.trim() || '-'
        };
      });
      
      // Generate timestamp for filename
      const timestamp = new Date().toISOString().slice(0,10);
      
      // Export to Excel
      exportToExcel(enhancedData, `جهات_الاتصال_${timestamp}.xlsx`);
    } catch (error) {
      console.error('Excel export error:', error);
      alert(`حدث خطأ أثناء تصدير ملف Excel: ${error instanceof Error ? error.message : 'خطأ غير معروف'}`);
    }
  };

  // Export contacts to PDF
  const handlePdfExport = async () => {
    try {
      // جلب جميع البيانات المفلترة (بدون ترقيم صفحات)
      const exportFilters = { ...filters, page: 1, limit: total || 10000 };
      const response = await contactsApi.getContacts(exportFilters);
      const allContacts = response.contacts;
      
      // Create a new PDF document
      const doc = new jsPDF({
        orientation: 'landscape',
        unit: 'mm',
        format: 'a4'
      });
      
      // Set document properties
      doc.setProperties({
        title: 'تقرير جهات الاتصال',
        subject: 'تقرير جهات الاتصال من نظام إدارة العملاء',
        author: 'نظام إدارة العملاء',
        keywords: 'عملاء، موردين، تقرير',
        creator: 'نظام إدارة العملاء'
      });
      
      // Page dimensions
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      
      // Add title
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(18);
      doc.setTextColor(41, 128, 185);
      doc.text('تقرير جهات الاتصال', pageWidth/2, 10, { align: "center" });
      
      // Add date
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      doc.setTextColor(100, 100, 100);
      
      const date = new Date().toLocaleDateString('ar-SA', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
      doc.text(`تاريخ التقرير: ${date}`, pageWidth - 15, 15, { align: "right" });
      
      // Add summary info
      doc.setFontSize(12);
      doc.setTextColor(52, 73, 94);
      doc.text(`إجمالي جهات الاتصال: ${allContacts.length}`, 15, 15);
      
      // Draw separator line
      doc.setDrawColor(41, 128, 185);
      doc.setLineWidth(0.5);
      doc.line(10, 20, pageWidth - 10, 20);
      
      // Refined headers with Arabic text
      const headers = [
        { header: 'الاسم', key: 'name' },
        { header: 'النوع', key: 'type' },
        { header: 'البريد الإلكتروني', key: 'email' },
        { header: 'الهاتف', key: 'phone' },
        { header: 'رقم الحساب', key: 'accountNumber' },
        { header: 'الفئة', key: 'category' },
        { header: 'الحالة', key: 'status' },
        { header: 'العنوان', key: 'address' }
      ];
      
      // Enhanced data transformation with Arabic labels
      const enhancedData = allContacts.map(contact => {
        // استخراج رقم الحساب إذا كان موجودًا - تحسين الطريقة
        const accountNumber = typeof contact.accountId === 'object' && contact.accountId?.accountNumber
          ? contact.accountId.accountNumber
          : (contact.linkedAccount ? 'مرتبط' : '-');
          
        return {
          name: contact.name,
          type: contact.type === 'customer' ? 'عميل' : 'مورد',
          email: contact.email || '-',
          phone: contact.phone || '-',
          accountNumber: accountNumber,
          category: contact.category === 'regular' ? 'عادي' :
                   contact.category === 'premium' ? 'مميز' : 'VIP',
          status: contact.status === 'active' ? 'نشط' : 'غير نشط',
          address: `${contact.address?.city || ''} ${contact.address?.country || ''}`.trim() || '-'
        };
      });
      
      // Setup table columns and data
      const tableColumns = headers.map(h => h.header);
      const tableData = enhancedData.map(row =>
        headers.map(h => row[h.key as keyof typeof row])
      );
      
      // Apply professional table styling
      autoTable(doc, {
        head: [tableColumns],
        body: tableData,
        startY: 25,
        theme: 'grid',
        styles: {
          fontSize: 10,
          cellPadding: 3,
          lineColor: [220, 220, 220],
          lineWidth: 0.1,
          halign: 'right', // Right align for Arabic
          textColor: [50, 50, 50],
          font: 'helvetica'
        },
        headStyles: {
          fillColor: [41, 128, 185],
          textColor: 255,
          fontStyle: 'bold',
          halign: 'center',
          fontSize: 11
        },
        alternateRowStyles: {
          fillColor: [245, 245, 245]
        },
        margin: { top: 25, right: 10, bottom: 10, left: 10 },
        didDrawPage: (data) => {
          // Add footer on each page
          doc.setFontSize(8);
          doc.setTextColor(150, 150, 150);
          const footerText = "© 2025 نظام إدارة جهات الاتصال - Aliphia Systems";
          doc.text(footerText, pageWidth/2, pageHeight - 10, { align: "center" });
          
          // Add page numbers
          doc.text(`الصفحة ${doc.getNumberOfPages()}`, pageWidth - 15, pageHeight - 10, { align: "right" });
        }
      });
      
      // Save with a timestamp and better filename
      const timestamp = new Date().toISOString().slice(0,10);
      doc.save(`تقرير_جهات_الاتصال_${timestamp}.pdf`);
      
    } catch (error) {
      console.error('PDF generation error:', error);
      alert(`حدث خطأ أثناء تصدير ملف PDF: ${error instanceof Error ? error.message : 'خطأ غير معروف'}`);
    }
  };

  return (
    <Box sx={{ width: '100%', maxWidth: '1800px', mx: 'auto', px: 2 }}>
      {/* Header Section */}
      <Box
        sx={{
          pb: 2,
          pt: 1,
          px: { xs: 2, sm: 0 },
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', sm: 'center' },
          gap: 2
        }}
      >
        <Typography
          variant="h5"
          sx={{
            fontWeight: 600,
            color: 'primary.main',
            position: 'relative',
            '&::after': {
              content: '""',
              position: 'absolute',
              bottom: -4,
              left: 0,
              width: '40px',
              height: '3px',
              backgroundColor: 'primary.main',
              borderRadius: '2px'
            }
          }}
        >
          جهات الاتصال
        </Typography>
        
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1,
            justifyContent: { xs: 'flex-start', sm: 'flex-end' },
            width: { xs: '100%', sm: 'auto' }
          }}
        >
          <Tooltip title="إضافة جديد" arrow>
            <Button
              startIcon={<AddIcon />}
              variant="contained"
              color="primary"
              onClick={() => navigate('/contacts/new')}
              disabled={!hasPermission('contacts', 'customers', 'create')}
              sx={{
                borderRadius: 2,
                px: 3,
                py: 1,
                fontWeight: 600,
                boxShadow: '0 4px 12px rgba(25, 118, 210, 0.2)',
                transition: 'all 0.2s',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 16px rgba(25, 118, 210, 0.3)'
                }
              }}
            >
              إضافة جهة اتصال
            </Button>
          </Tooltip>
          
          <Tooltip title="تصدير اكسل" arrow>
            <Button
              startIcon={<DownloadIcon />}
              onClick={handleExcelExport}
              sx={{
                color: 'success.main',
                borderColor: 'success.main',
                borderRadius: 2,
                px: 3,
                fontWeight: 500,
                transition: 'all 0.2s',
                '&:hover': {
                  bgcolor: 'rgba(76, 175, 80, 0.08)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 8px rgba(76, 175, 80, 0.15)'
                },
                '&.Mui-disabled': {
                  opacity: 0.5,
                  color: 'rgba(76, 175, 80, 0.5)',
                  borderColor: 'rgba(76, 175, 80, 0.3)'
                }
              }}
              variant="outlined"
              size="medium"
            >
              تصدير اكسل
            </Button>
          </Tooltip>
          
          <Tooltip title="تصدير PDF" arrow>
            <Button
              startIcon={<PdfIcon />}
              onClick={handlePdfExport}
              sx={{
                color: 'primary.main',
                borderColor: 'primary.main',
                borderRadius: 2,
                px: 3,
                fontWeight: 500,
                transition: 'all 0.2s',
                '&:hover': {
                  bgcolor: 'rgba(33, 150, 243, 0.08)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 8px rgba(33, 150, 243, 0.15)'
                }
              }}
              variant="outlined"
              size="medium"
            >
              تصدير PDF
            </Button>
          </Tooltip>
        </Box>

        {/* Advanced Filters Section */}
        <Card
          elevation={0}
          sx={{
            bgcolor: 'white',
            borderRadius: 3,
            border: '1px solid rgba(0,0,0,0.08)',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            overflow: 'visible',
            position: 'relative',
            mt: 2,
            mb: 3
          }}
        >
          {/* Filter Badge */}
          <Box
            sx={{
              position: 'absolute',
              top: -12,
              right: 20,
              bgcolor: 'primary.main',
              color: 'white',
              py: 0.5,
              px: 2.5,
              borderRadius: '20px',
              fontSize: '0.75rem',
              fontWeight: 600,
              boxShadow: '0 2px 8px rgba(25, 118, 210, 0.3)',
              display: 'flex',
              alignItems: 'center',
              gap: 0.8,
              zIndex: 10
            }}
          >
            <SearchIcon fontSize="small" />
            الفلاتر
          </Box>
          
          {/* Active Filters Display */}
          {(filters.search || filters.type || filters.category || filters.status || filters.branch) && (
            <Box
              sx={{
                position: 'absolute',
                top: -18,
                left: 24,
                display: 'flex',
                flexWrap: 'wrap',
                gap: 1,
                zIndex: 10
              }}
            >
              {filters.search && (
                <Chip
                  size="small"
                  label={`بحث: ${filters.search}`}
                  onDelete={() => handleFilterChange('search', '')}
                  color="info"
                  variant="filled"
                  sx={{
                    borderRadius: '30px',
                    fontWeight: 500,
                    py: 0.5,
                    boxShadow: '0 3px 6px rgba(0,0,0,0.1)'
                  }}
                />
              )}
              
              {filters.type && (
                <Chip
                  size="small"
                  label={`النوع: ${filters.type === 'customer' ? 'عميل' : 'مورد'}`}
                  onDelete={() => handleFilterChange('type', '')}
                  color="success"
                  variant="filled"
                  sx={{
                    borderRadius: '30px',
                    fontWeight: 500,
                    py: 0.5,
                    boxShadow: '0 3px 6px rgba(0,0,0,0.1)'
                  }}
                />
              )}
              
              {filters.category && (
                <Chip
                  size="small"
                  label={`الفئة: ${filters.category === 'regular' ? 'عادي' : filters.category === 'premium' ? 'مميز' : 'VIP'}`}
                  onDelete={() => handleFilterChange('category', '')}
                  color="secondary"
                  variant="filled"
                  sx={{
                    borderRadius: '30px',
                    fontWeight: 500,
                    py: 0.5,
                    boxShadow: '0 3px 6px rgba(0,0,0,0.1)'
                  }}
                />
              )}
              
              {filters.status && (
                <Chip
                  size="small"
                  label={`الحالة: ${filters.status === 'active' ? 'نشط' : 'غير نشط'}`}
                  onDelete={() => handleFilterChange('status', '')}
                  color="error"
                  variant="filled"
                  sx={{
                    borderRadius: '30px',
                    fontWeight: 500,
                    py: 0.5,
                    boxShadow: '0 3px 6px rgba(0,0,0,0.1)'
                  }}
                />
              )}
              
              {filters.branch && (
                <Chip
                  size="small"
                  label={(() => {
                    const branch = branches.find(b => b._id === filters.branch);
                    return `الفرع: ${branch ? branch.name : filters.branch}`;
                  })()}
                  onDelete={() => handleFilterChange('branch', '')}
                  sx={{
                    borderRadius: '30px',
                    fontWeight: 500,
                    py: 0.5,
                    bgcolor: 'purple',
                    color: 'white',
                    boxShadow: '0 3px 6px rgba(0,0,0,0.1)'
                  }}
                />
              )}
            </Box>
          )}
          
          <CardContent
            sx={{
              p: 3,
              pt: 3.5,
              pb: 3
            }}
          >
            <Grid container spacing={2.5} alignItems="center">
              <Grid item xs={12} sm={8} md={6}>
                <TextField
                  fullWidth
                  placeholder="البحث بالاسم، البريد أو الهاتف..."
                  variant="outlined"
                  size="medium"
                  value={filters.search || ''}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  InputProps={{
                    startAdornment: <SearchIcon color="action" sx={{ mr: 1 }} />,
                    sx: {
                      borderRadius: 2,
                      bgcolor: '#fafafa',
                      '&:hover': {
                        bgcolor: 'white'
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(0,0,0,0.1)'
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'primary.main'
                      }
                    }
                  }}
                />
              </Grid>
              
              {!hideTypeFilter && (
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    select
                    label="النوع"
                    size="medium"
                    value={filters.type || ''}
                    onChange={(e) => handleFilterChange('type', e.target.value)}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        bgcolor: '#fafafa',
                        '&:hover': {
                          bgcolor: 'white'
                        },
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'rgba(0,0,0,0.1)'
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'success.main'
                        }
                      }
                    }}
                  >
                    <MenuItem value="">جميع الأنواع</MenuItem>
                    <MenuItem value="customer">عميل</MenuItem>
                    <MenuItem value="supplier">مورد</MenuItem>
                  </TextField>
                </Grid>
              )}
              
              <Grid item xs={12} sm={6} md={hideTypeFilter ? 4 : 3}>
                <TextField
                  fullWidth
                  select
                  label="الفئة"
                  size="medium"
                  value={filters.category || ''}
                  onChange={(e) => handleFilterChange('category', e.target.value)}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      bgcolor: '#fafafa',
                      '&:hover': {
                        bgcolor: 'white'
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(0,0,0,0.1)'
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'secondary.main'
                      }
                    }
                  }}
                >
                  <MenuItem value="">جميع الفئات</MenuItem>
                  <MenuItem value="regular">عادي</MenuItem>
                  <MenuItem value="premium">مميز</MenuItem>
                  <MenuItem value="vip">VIP</MenuItem>
                </TextField>
              </Grid>
              
              <Grid item xs={12} sm={6} md={hideTypeFilter ? 4 : 3}>
                <TextField
                  fullWidth
                  select
                  label="الحالة"
                  size="medium"
                  value={filters.status || ''}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      bgcolor: '#fafafa',
                      '&:hover': {
                        bgcolor: 'white'
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(0,0,0,0.1)'
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'error.main'
                      }
                    }
                  }}
                >
                  <MenuItem value="">جميع الحالات</MenuItem>
                  <MenuItem value="active">نشط</MenuItem>
                  <MenuItem value="inactive">غير نشط</MenuItem>
                </TextField>
              </Grid>
              
              <Grid item xs={12} sm={6} md={hideTypeFilter ? 4 : 3}>
                <TextField
                  fullWidth
                  select
                  label="الفرع"
                  size="medium"
                  value={filters.branch || ''}
                  onChange={(e) => handleFilterChange('branch', e.target.value)}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      bgcolor: '#fafafa',
                      '&:hover': {
                        bgcolor: 'white'
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(0,0,0,0.1)'
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'purple'
                      }
                    }
                  }}
                >
                  <MenuItem value="">جميع الفروع</MenuItem>
                  {branches.map((branch) => (
                    <MenuItem key={branch._id} value={branch._id}>
                      {branch.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Box>

      {/* Table/Cards Section - Full Width */}
      <Box sx={{ px: 0, pb: 4, pt: 0, width: '100%' }}>
        {isLoading ? (
          <Box sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            py: 10
          }}>
            <Typography variant="body1" sx={{ color: 'text.secondary', mr: 2 }}>
              جاري تحميل البيانات...
            </Typography>
          </Box>
        ) : contacts.length === 0 ? (
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            py: 10,
            color: 'text.secondary'
          }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              لا توجد نتائج مطابقة للبحث
            </Typography>
            <Typography variant="body2">
              حاول تغيير معايير البحث أو إضافة جهات اتصال جديدة
            </Typography>
          </Box>
        ) : (
          // Always show Table View regardless of screen size
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <TableContainer
              component={Paper}
              sx={{
                mt: 0,
                borderRadius: '12px',
                overflowX: 'auto',
                overflowY: 'hidden',
                WebkitOverflowScrolling: 'touch',
                boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                border: '1px solid rgba(0,0,0,0.04)',
                width: '100%', // Changed to full width
                maxWidth: '100%'
              }}
            >
              <Table stickyHeader aria-label="جدول جهات الاتصال" sx={{ tableLayout: 'auto', minWidth: 1000 }}>
              <TableHead>
                <TableRow>
                  {[
                    'الاسم',
                    'النوع',
                    'البريد الإلكتروني', // Always show email column
                    'الهاتف', // Always show phone column
                    'الفرع',
                    'رقم الحساب', // إضافة عمود رقم الحساب
                    'الفئة',
                    'الحالة',
                    'الإجراءات'
                  ].map((header, index) => (
                    <TableCell
                      key={index}
                      align="center"
                      sx={{
                        bgcolor: 'primary.main',
                        color: 'white',
                        fontWeight: 600,
                        fontSize: '0.95rem',
                        borderBottom: '2px solid #1976d2',
                        py: 2.5,
                        textAlign: 'center',
                        position: 'relative',
                        minWidth: header === 'البريد الإلكتروني' ? 180 : 'auto',
                        width: header === 'الإجراءات' ? 150 : 'auto',
                        '&::after': index < 6 ? {
                          content: '""',
                          position: 'absolute',
                          right: 0,
                          top: '50%',
                          transform: 'translateY(-50%)',
                          height: '70%',
                          width: '1px',
                          backgroundColor: 'rgba(255, 255, 255, 0.3)'
                        } : {},
                        letterSpacing: '0.5px'
                      }}
                    >
                      {header}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {contacts.map((contact, index) => (
                  <TableRow
                    key={contact._id}
                    sx={{
                      bgcolor: index % 2 === 0 ? 'white' : 'rgba(245, 247, 250, 1)',
                      '&:hover': {
                        bgcolor: 'rgba(25, 118, 210, 0.04)',
                        boxShadow: 'inset 0 0 0 1px rgba(25, 118, 210, 0.1)'
                      },
                      transition: 'all 0.2s ease'
                    }}
                  >
                    {/* Name Cell */}
                    <TableCell
                      align="center"
                      sx={{
                        fontSize: '0.95rem',
                        fontWeight: 500,
                        color: '#2c3e50',
                        borderBottom: '1px solid rgba(224, 224, 224, 0.5)',
                        py: 2,
                        px: 2,
                        position: 'relative',
                        whiteSpace: 'nowrap',
                        maxWidth: { xs: '100px', sm: '120px', md: '150px' },
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        '&::after': {
                          content: '""',
                          position: 'absolute',
                          right: 0,
                          top: '50%',
                          transform: 'translateY(-50%)',
                          height: '70%',
                          width: '1px',
                          backgroundColor: 'rgba(0, 0, 0, 0.05)'
                        }
                      }}
                    >
                      {contact.name}
                    </TableCell>
                    
                    {/* Type Cell */}
                    <TableCell
                      align="center"
                      sx={{
                        borderBottom: '1px solid rgba(224, 224, 224, 0.5)',
                        py: 2,
                        px: 2,
                        position: 'relative',
                        '&::after': {
                          content: '""',
                          position: 'absolute',
                          right: 0,
                          top: '50%',
                          transform: 'translateY(-50%)',
                          height: '70%',
                          width: '1px',
                          backgroundColor: 'rgba(0, 0, 0, 0.05)'
                        }
                      }}
                    >
                      <Chip
                        label={contact.type === 'customer' ? 'عميل' : 'مورد'}
                        size="small"
                        sx={{
                          borderRadius: 1,
                          fontWeight: 500,
                          px: 0.5,
                          bgcolor: contact.type === 'customer' ? 'rgba(76, 175, 80, 0.1)' : 'rgba(33, 150, 243, 0.1)',
                          color: contact.type === 'customer' ? 'success.dark' : 'info.dark',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                          '& .MuiChip-label': { px: 1.5 }
                        }}
                      />
                    </TableCell>
                    
                    {/* Email Cell - Always visible */}
                    <TableCell
                      align="center"
                      sx={{
                        fontSize: '0.95rem',
                        borderBottom: '1px solid rgba(224, 224, 224, 0.5)',
                        py: 2,
                        px: 2,
                        color: 'info.main',
                        position: 'relative',
                        textAlign: 'center',
                        maxWidth: { xs: '120px', sm: '150px', lg: '200px' },
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        '&::after': {
                          content: '""',
                          position: 'absolute',
                          right: 0,
                          top: '50%',
                          transform: 'translateY(-50%)',
                          height: '70%',
                          width: '1px',
                          backgroundColor: 'rgba(0, 0, 0, 0.05)'
                        }
                      }}
                    >
                      {contact.email || '-'}
                    </TableCell>
                    
                    {/* Phone Cell - Always visible */}
                    <TableCell
                      align="center"
                      sx={{
                        fontSize: '0.95rem',
                        borderBottom: '1px solid rgba(224, 224, 224, 0.5)',
                        py: 2,
                        px: 2,
                        fontFamily: 'monospace', // Better for numbers
                        letterSpacing: '0.5px',
                        position: 'relative',
                        '&::after': {
                          content: '""',
                          position: 'absolute',
                          right: 0,
                          top: '50%',
                          transform: 'translateY(-50%)',
                          height: '70%',
                          width: '1px',
                          backgroundColor: 'rgba(0, 0, 0, 0.05)'
                        }
                      }}
                    >
                      {contact.phone || '-'}
                    </TableCell>
                    
                    {/* Branches Cell */}
                    <TableCell
                      align="center"
                      sx={{
                        fontSize: '0.95rem',
                        borderBottom: '1px solid rgba(224, 224, 224, 0.5)',
                        py: 2,
                        px: 2,
                        position: 'relative',
                        '&::after': {
                          content: '""',
                          position: 'absolute',
                          right: 0,
                          top: '50%',
                          transform: 'translateY(-50%)',
                          height: '70%',
                          width: '1px',
                          backgroundColor: 'rgba(0, 0, 0, 0.05)'
                        }
                      }}
                    >
                      {contact.branches && contact.branches.length > 0
                        ? <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                            {contact.branches.length > 1
                              ? <Chip
                                  label={`${contact.branches.length} فروع`}
                                  size="small"
                                  color="primary"
                                  variant="outlined"
                                  title={contact.branches.map(branchId => {
                                    const branch = branches.find(b => b._id === branchId);
                                    return branch ? branch.name : branchId;
                                  }).join(', ')}
                                />
                              : <Chip
                                  label={(() => {
                                    if (!contact.branches || contact.branches.length === 0) return '-';
                                    const branch = branches.find(b => b._id === contact.branches?.[0]);
                                    return branch ? branch.name : contact.branches?.[0] || '-';
                                  })()}
                                  size="small"
                                  color="primary"
                                  variant="outlined"
                                />
                            }
                          </Box>
                        : '-'
                      }
                    </TableCell>
                    
                    {/* Account Number Cell - Added */}
                    <TableCell
                      align="center"
                      sx={{
                        fontSize: '0.95rem',
                        borderBottom: '1px solid rgba(224, 224, 224, 0.5)',
                        py: 2,
                        px: 2,
                        position: 'relative',
                        fontFamily: 'monospace', // Better for account numbers
                        letterSpacing: '0.5px',
                        color: 'primary.dark',
                        fontWeight: contact.linkedAccount ? 600 : 400,
                        '&::after': {
                          content: '""',
                          position: 'absolute',
                          right: 0,
                          top: '50%',
                          transform: 'translateY(-50%)',
                          height: '70%',
                          width: '1px',
                          backgroundColor: 'rgba(0, 0, 0, 0.05)'
                        }
                      }}
                    >
                      {/* تحسين طريقة عرض رقم الحساب */}
                      {typeof contact.accountId === 'object' && contact.accountId?.accountNumber ? (
                        // إذا كان الحساب كائن وله رقم حساب، عرض رقم الحساب
                        contact.accountId.accountNumber
                      ) : contact.linkedAccount ? (
                        // إذا كان الحساب مرتبط ولكن لا توجد معلومات مفصلة، عرض "مرتبط"
                        <Chip
                          label="مرتبط"
                          size="small"
                          color="primary"
                          variant="outlined"
                          sx={{
                            borderRadius: 1,
                            fontWeight: 500,
                            px: 0.5,
                            fontSize: '0.75rem'
                          }}
                        />
                      ) : (
                        // لا يوجد حساب مرتبط
                        '-'
                      )}
                    </TableCell>
                    
                    {/* Category Cell */}
                    <TableCell
                      align="center"
                      sx={{
                        borderBottom: '1px solid rgba(224, 224, 224, 0.5)',
                        py: 2,
                        px: 2,
                        position: 'relative',
                        '&::after': {
                          content: '""',
                          position: 'absolute',
                          right: 0,
                          top: '50%',
                          transform: 'translateY(-50%)',
                          height: '70%',
                          width: '1px',
                          backgroundColor: 'rgba(0, 0, 0, 0.05)'
                        }
                      }}
                    >
                      <Chip
                        label={
                          contact.category === 'regular' ? 'عادي' :
                          contact.category === 'premium' ? 'مميز' : 'VIP'
                        }
                        size="small"
                        sx={{
                          borderRadius: 1,
                          fontWeight: 500,
                          boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                          '& .MuiChip-label': { px: 1.5 },
                          bgcolor: contact.category === 'regular' ? 'rgba(158, 158, 158, 0.1)' :
                                  contact.category === 'premium' ? 'rgba(33, 150, 243, 0.1)' : 'rgba(233, 30, 99, 0.1)',
                          color: contact.category === 'regular' ? 'text.secondary' :
                                contact.category === 'premium' ? 'primary.dark' : 'secondary.dark'
                        }}
                      />
                    </TableCell>
                    
                    {/* Status Cell */}
                    <TableCell
                      align="center"
                      sx={{
                        borderBottom: '1px solid rgba(224, 224, 224, 0.5)',
                        py: 2,
                        px: 2,
                        position: 'relative',
                        '&::after': {
                          content: '""',
                          position: 'absolute',
                          right: 0,
                          top: '50%',
                          transform: 'translateY(-50%)',
                          height: '70%',
                          width: '1px',
                          backgroundColor: 'rgba(0, 0, 0, 0.05)'
                        }
                      }}
                    >
                      <Chip
                        label={contact.status === 'active' ? 'نشط' : 'غير نشط'}
                        size="small"
                        sx={{
                          borderRadius: 1,
                          fontWeight: 500,
                          boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                          '& .MuiChip-label': { px: 1.5 },
                          bgcolor: contact.status === 'active' ? 'rgba(76, 175, 80, 0.1)' : 'rgba(244, 67, 54, 0.1)',
                          color: contact.status === 'active' ? 'success.dark' : 'error.dark'
                        }}
                      />
                    </TableCell>
                    
                    {/* Actions Cell */}
                    <TableCell
                      align="center"
                      sx={{
                        borderBottom: '1px solid rgba(224, 224, 224, 0.5)',
                        py: 2,
                        px: 2
                      }}
                    >
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                        <Tooltip title="عرض التفاصيل" arrow>
                          <IconButton
                            size="small"
                            onClick={() => navigate(`/contacts/${contact._id}`)}
                            sx={{
                              color: 'info.main',
                              bgcolor: 'rgba(33, 150, 243, 0.1)',
                              width: 34,
                              height: 34,
                              '&:hover': {
                                bgcolor: 'rgba(33, 150, 243, 0.2)',
                                transform: 'translateY(-2px)',
                                boxShadow: '0 3px 8px rgba(33, 150, 243, 0.2)'
                              },
                              transition: 'all 0.2s'
                            }}
                          >
                            <SearchIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        
                        {hasPermission('contacts', 'customers', 'edit') && (
                          <Tooltip title="تعديل" arrow>
                            <IconButton
                              size="small"
                              onClick={() => navigate(`/contacts/edit/${contact._id}`)}
                              sx={{
                                color: 'warning.main',
                                bgcolor: 'rgba(255, 152, 0, 0.1)',
                                width: 34,
                                height: 34,
                                '&:hover': {
                                  bgcolor: 'rgba(255, 152, 0, 0.2)',
                                  transform: 'translateY(-2px)',
                                  boxShadow: '0 3px 8px rgba(255, 152, 0, 0.2)'
                                },
                                transition: 'all 0.2s'
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        
                        {hasPermission('contacts', 'customers', 'delete') && (
                          <Tooltip title="حذف" arrow>
                            <IconButton
                              size="small"
                              onClick={() => handleDelete(contact._id)}
                              sx={{
                                color: 'error.main',
                                bgcolor: 'rgba(244, 67, 54, 0.1)',
                                width: 34,
                                height: 34,
                                '&:hover': {
                                  bgcolor: 'rgba(244, 67, 54, 0.2)',
                                  transform: 'translateY(-2px)',
                                  boxShadow: '0 3px 8px rgba(244, 67, 54, 0.2)'
                                },
                                transition: 'all 0.2s'
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={total}
              page={(filters.page || 1) - 1}
              onPageChange={handlePageChange}
              rowsPerPage={filters.limit || 10}
              onRowsPerPageChange={handleLimitChange}
              rowsPerPageOptions={[10, 25, 50, 100]}
              labelRowsPerPage="عدد النتائج:"
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} من ${count}`}
              sx={{
                bgcolor: 'white',
                borderTop: '2px solid rgba(224, 224, 224, 0.5)',
                py: 2,
                px: 3,
                '& .MuiTablePagination-toolbar': {
                  borderRadius: 2,
                  p: 1.5,
                  backgroundColor: 'rgba(0, 0, 0, 0.02)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  flexWrap: 'wrap',
                  gap: 1
                },
                '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                  fontWeight: 500,
                  color: '#2c3e50',
                  fontSize: '0.95rem',
                  m: 0
                },
                '& .MuiTablePagination-select': {
                  borderRadius: 1.5,
                  mx: 1,
                  border: '1px solid rgba(0, 0, 0, 0.08)',
                  py: 0.5,
                  pl: 1.5,
                  pr: 0.5
                },
                '& .MuiIconButton-root': {
                  color: 'primary.main',
                  width: 36,
                  height: 36,
                  borderRadius: 1.5,
                  '&:hover': {
                    bgcolor: 'rgba(25, 118, 210, 0.1)',
                    transform: 'translateY(-1px)',
                    boxShadow: '0 2px 5px rgba(25, 118, 210, 0.15)'
                  },
                  '&.Mui-disabled': {
                    opacity: 0.5,
                    bgcolor: 'transparent',
                    boxShadow: 'none'
                  },
                  transition: 'all 0.2s'
                },
                '& .MuiTablePagination-actions': {
                  display: 'flex',
                  gap: 0.5
                }
              }}
            />
            </TableContainer>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default ContactList;
